import { Box, Container, Divider } from '@mui/material';
import { Button, Text } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { useNavigate } from 'react-router-dom';
import GitHubIcon from '@mui/icons-material/GitHub';
import GoogleIcon from '@mui/icons-material/Google';
import { useEffect, useState } from 'react';
import { useMiddlewareClient } from '@qwickforge/react-client';

export default function LoginPage() {
  const { signInWithProvider, user, session } = useAuth();
  const navigate = useNavigate();
  const client = useMiddlewareClient();
  const [enabledProviders, setEnabledProviders] = useState<string[]>([]);

  // Get enabled providers from auth service client
  useEffect(() => {
    // Access the Supabase provider to get enabled providers
    const supabaseProvider = (window as any).__supabaseAuthProvider;
    if (supabaseProvider && typeof supabaseProvider.getEnabledProviders === 'function') {
      setEnabledProviders(supabaseProvider.getEnabledProviders());
    } else {
      // Default to both if not configured
      setEnabledProviders(['github', 'google']);
    }
  }, []);

  // Redirect logged-in users
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (user && session?.accessToken && client) {
        try {
          const data = await client.devices.list();
          const devices = Array.isArray(data) ? data : data.devices || [];

          if (devices.length > 0) {
            navigate('/dashboard');
          } else {
            navigate('/setup/device');
          }
        } catch (error) {
          console.error('Failed to check devices:', error);
          navigate('/setup/device');
        }
      }
    };

    checkAndRedirect();
  }, [user, session, client, navigate]);

  const handleProviderLogin = async (provider: string) => {
    try {
      await signInWithProvider({
        provider,
        redirectTo: `${window.location.origin}/auth/callback`,
      });
    } catch (error) {
      console.error(`${provider} login failed:`, error);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Text variant="h4" gutterBottom customColor="var(--theme-text-primary)" content="Sign in to QwickForge IDE" />
          <Text variant="body1" customColor="var(--theme-text-secondary)" content="Run AI agents on your own hardware" />
        </Box>

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {enabledProviders.includes('github') && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GitHubIcon sx={{ color: 'var(--theme-text-secondary)' }} />}
              onClick={() => handleProviderLogin('github')}
              sx={{ mb: 2, py: 1.5 }}
            >
              Continue with GitHub
            </Button>
          )}

          {enabledProviders.includes('google') && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GoogleIcon sx={{ color: 'var(--theme-text-secondary)' }} />}
              onClick={() => handleProviderLogin('google')}
              sx={{ mb: 2, py: 1.5 }}
            >
              Continue with Google
            </Button>
          )}

          <Divider sx={{ my: 3 }}>
            <Text variant="body2" customColor="var(--theme-text-secondary)" content="Enterprise" />
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Text variant="body2" customColor="var(--theme-text-secondary)" content="Need email/password login for your company?" />
            <br />
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/contact-sales')}
              sx={{ mt: 1 }}
            >
              Contact Sales
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Text
            variant="caption"
            customColor="var(--theme-text-secondary)"
            content={
              <>
                By signing in, you agree to our{' '}
                <a href="/legal/terms">Terms of Service</a>
                {' '}and{' '}
                <a href="/legal/privacy">Privacy Policy</a>
              </>
            }
          />
        </Box>
      </Box>
    </Container>
  );
}
